import React from 'react';
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { SafetyDashboardRow } from '../../services/reportsApi';
import { buildRiskPatternTitle } from '../../domain/safetyCases/riskPatternPortfolio';
import {
  NewActivityDecisionPanel,
} from './NewActivityDecisionPanel';
import type { NewActivityDecisionKind } from '../../domain/safetyCases/workspaceNewActivityHandling';

function formatWhen(value?: string | null): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(parsed);
}

export function NewActivityAttentionDrawer({
  open,
  row,
  onClose,
  onReviewEvidence,
  canDecide = false,
  decisionBusy = false,
  decisionError = null,
  actorUser = null,
  onDecision,
}: {
  open: boolean;
  row: SafetyDashboardRow | null;
  onClose: () => void;
  onReviewEvidence: () => void;
  canDecide?: boolean;
  decisionBusy?: boolean;
  decisionError?: string | null;
  actorUser?: { username?: string | null; email?: string | null } | null;
  onDecision?: (kind: NewActivityDecisionKind, reviewerNote: string) => void;
}) {
  const active = open && row ? row : null;
  const title = active ? buildRiskPatternTitle(active) : '';
  const workState = active
    ? (String(
      active.customer_attention_work_state
      || active.customer_control_stage
      || active.safety_case_status_label
      || active.workflow_status_label
      || 'New activity',
    ).trim() || 'New activity')
    : '';

  return (
    <Drawer
      anchor="right"
      open={Boolean(active)}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 560, md: 640 },
          maxWidth: '100%',
          p: 2.5,
          bgcolor: '#f8fafc',
        },
      }}
    >
      {active ? (
        <Stack spacing={2.5} sx={{ height: '100%' }} data-testid="new-activity-attention-drawer">
          <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                New activity after sign-off
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.25 }} data-testid="new-activity-drawer-title">
                {title}
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                <Chip size="small" color="warning" label={workState} data-testid="new-activity-drawer-work-state" />
                <Chip size="small" variant="outlined" label="Signed Scan frozen" />
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {`Latest observation ${formatWhen(active.latest_observation_at || active.updated_at)}`}
              </Typography>
            </Box>
            <IconButton aria-label="Close new activity drawer" onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'warning.light',
              bgcolor: 'background.paper',
            }}
          >
            <NewActivityDecisionPanel
              row={active}
              canDecide={canDecide}
              busy={decisionBusy}
              error={decisionError}
              actorUser={actorUser}
              onReviewEvidence={onReviewEvidence}
              onSubmit={(kind, reviewerNote) => onDecision?.(kind, reviewerNote)}
            />
          </Box>
        </Stack>
      ) : null}
    </Drawer>
  );
}

export default NewActivityAttentionDrawer;
